<template>
  <div class="pob" data-test="post-op-ack-bar">
    <div v-if="done" class="pob-thanks" role="status" data-test="post-op-ack-thanks">
      <i class="fa fa-check-circle" aria-hidden="true"></i>
      <span>{{ tx('postOpAckThanks') }}</span>
    </div>
    <template v-else>
      <div v-if="failure" class="pob-error" role="alert" data-test="post-op-ack-error">
        {{ failure === 'retry' ? tx('postOpAckRetry') : tx('postOpAckUnavailable') }}
      </div>
      <button type="button" class="pob-btn" :disabled="sending" data-test="post-op-ack-button" @click="ack">
        <i v-if="sending" class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>
        {{ sending ? tx('postOpAckSending') : tx('postOpAckButton') }}
      </button>
    </template>
  </div>
</template>

<script>
import { acknowledgePostOp } from '@/api/postOp';
import { ackFailureKind } from '@/utils/postOp/errors';
import { t } from '../utils/systemTranslations';

/**
 * The patient's "I've read these instructions" bar on a post-op after-care link (plan §5c). ViewForm mounts it only when ITS OWN
 * getLongUrl result says `linkMode === 'postop'`.
 *
 *   tap → POST /get-long-url/{urlID} { urlID }   (public; the ack is per FORM — the link row is one form; the path is shared
 *         with the GET getLongUrl because the dev API Gateway is out of resources — see src/api/postOp.js)
 *     200 → "Thanks — the office has been notified. You can open this link again anytime."
 *     503 / 500 / 429 / network → "please tap again", the button STAYS (the server released its claim, so a retry records it)
 *     404 / 403 → tapping again cannot help: say so, keep the button
 *
 * ⛔ PHI: this component knows only the link id and a language. It renders no patient field and sends none.
 */
export default {
  name: 'PostOpAckBar',
  props: {
    urlId: { type: String, required: true },
    language: { type: String, default: 'en' },
  },
  data() {
    return { sending: false, done: false, failure: '' };
  },
  methods: {
    tx(key) { return t(key, this.language); },
    async ack() {
      if (this.sending || this.done) return;
      this.sending = true;
      this.failure = '';
      try {
        await acknowledgePostOp(this.urlId);
        this.done = true;
      } catch (e) {
        this.failure = ackFailureKind(e && e.status);
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// position: fixed, not sticky: the patient page sets `overflow-x: hidden` on its root, which turns it into a scroll container
// and defeats `position: sticky; bottom: 0` (the bar would scroll away with the content).
.pob {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -6px 20px rgba(12, 15, 14, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.pob-btn {
  width: 100%;
  max-width: 480px;
  min-height: 48px;
  padding: 0 20px;
  border: 0;
  border-radius: 12px;
  background: #0f7259;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) { background: #0b5c49; }
  &:disabled { opacity: 0.7; cursor: default; }
  &:focus-visible { outline: 3px solid rgba(15, 114, 89, 0.4); outline-offset: 2px; }
  i { margin-right: 8px; }
}
.pob-error {
  max-width: 480px;
  font-size: 14.5px;
  font-weight: 600;
  text-align: center;
  color: #a5292e;
}
.pob-thanks {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  max-width: 480px;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  color: #0b5c49;

  i { font-size: 22px; color: #0f7259; }
}
</style>
